import React, { useMemo } from 'react';
import styled from 'styled-components';
import LoadingAvatar from '../../../components/LoadingAvatar';
import { Fonts, Palette } from '../../../components/utils';
import { LOADING_BLUE } from '../../../utils/colorCode';
import { MdError } from 'react-icons/md';
import defaultCcLogo from '../../../assets/icons/payment/default_cc_logo.png';
import { resolveCreditCardLogo } from './creditCardLogoAssets';

const PaymentMethodBlock = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
`;

const PaymentMethodRow = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardBrandIconWrap = styled.div`
  width: 40px;
  height: 28px;
  border-radius: 4px;
  background: ${Palette.WHITE};
  border: 1px solid ${Palette.STROKE_GREY};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

function CardBrandLogo({ display }) {
  const logoSrc = useMemo(
    () =>
      resolveCreditCardLogo({
        bankName: display.bankName,
        bankNameShort: display.bankNameShort,
        cardBackgroundURL: display.cardBackgroundURL
      }),
    [display.bankName, display.bankNameShort, display.cardBackgroundURL]
  );

  return (
    <img
      src={logoSrc}
      alt=""
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = defaultCcLogo;
      }}
    />
  );
}

const CardDetails = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const CardBrandName = styled.span`
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const CardNumber = styled.span`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

const CreditCardErrorNotice = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${Palette.BACKGROUND_TAG_RED};
  border: 1px solid ${Palette.ERROR}33;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const CreditCardErrorText = styled.span`
  flex: 1;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 20px;
  color: ${Palette.ERROR};
`;

const ExpiredNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const ExpiredNoteText = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 20px;
  color: ${Palette.ERROR};
`;

function formatMaskedCardNumber(lastFour) {
  return `**** **** **** ${lastFour}`;
}

function BookingDefaultCreditCardV2({ t, display, loading = false, showExpiredError = false }) {
  const expiredMessage = t('book_amenity_credit_card_expired', {
    defaultValue: 'Credit Card Expired'
  });

  if (loading) {
    return (
      <PaymentMethodBlock>
        <PaymentMethodRow>
          <LoadingAvatar color={LOADING_BLUE} />
        </PaymentMethodRow>
      </PaymentMethodBlock>
    );
  }

  if (!display?.lastFour) {
    if (!showExpiredError) {
      return null;
    }

    return (
      <PaymentMethodBlock>
        <CreditCardErrorNotice role="alert">
          <MdError size={24} color={Palette.ERROR} style={{ flexShrink: 0 }} />
          <CreditCardErrorText>{expiredMessage}</CreditCardErrorText>
        </CreditCardErrorNotice>
      </PaymentMethodBlock>
    );
  }

  return (
    <PaymentMethodBlock aria-label={t('Payment Method', { defaultValue: 'Payment Method' })}>
      <PaymentMethodRow>
        <CardBrandIconWrap>
          <CardBrandLogo display={display} />
        </CardBrandIconWrap>
        <CardDetails>
          <CardBrandName>{display.brandLabel}</CardBrandName>
          <CardNumber>{formatMaskedCardNumber(display.lastFour)}</CardNumber>
        </CardDetails>
      </PaymentMethodRow>
      {showExpiredError ? (
        <ExpiredNote role="alert">
          <MdError size={20} color={Palette.ERROR} style={{ flexShrink: 0 }} />
          <ExpiredNoteText>{expiredMessage}</ExpiredNoteText>
        </ExpiredNote>
      ) : null}
    </PaymentMethodBlock>
  );
}

export default BookingDefaultCreditCardV2;
